import { z } from 'zod';
import {
  memberCarePreferencesSummaryClientSchema,
  memberCarePreferencesSummarySchema,
} from '../member-care-preferences/schema';
import { careAppointmentAttendanceSchema } from '../api/telemedicine';
import { registrationAcuitySchema } from '../care/acuity';
import { careAppointmentModalitySchema } from '../care/appointments';
import { tolerantCareCallReasonSchema } from '../care/care-call-reason';
import { routableCareCallTypeSchema } from '../care/care-call-types';
import { apiEnvelope } from '../envelope';
import { memberDetailChangeSchema } from './detail-changes';
import {
  CARE_PATHWAY_TIER,
  CARE_TRACK,
  GOAL_STATUS,
  PROGRAM_END_NOTE_MAX_LENGTH,
  programEndReasonSchema,
} from '../programs/schema';
import { ianaTimezoneSchema } from '../timezone';
import { MEMBER_STATUS } from './status';
import { MEMBER_PROFILE_IDENTITY_TEXT_MAX } from '../member-profiles/schema';
import {
  activeEscalationSourceSchema,
  ESCALATION_CONTACT_ATTEMPTS_LIST_LIMIT,
  escalationStatusSchema,
} from '../escalations/schema';
import { e164PhoneSchema } from '../eligibility/schema';
import { COUNTRY_CODE_PATTERN, DEFAULT_COUNTRY_CODE, memberAddressKindSchema } from './addresses';

import { escalationClaimSchema } from '../escalations/claims';
import {
  REPORTED_WITHDRAWAL_SYMPTOM,
  reportedWithdrawalSymptomSchema,
} from '../onboarding-sessions/withdrawal-symptoms';
// camelCase wire projection (ADR-066) of the Galapagos-owned member-detail
// fields Atlas reads Galapagos-first. A demographic/PII field graduates onto
// this read only once Galapagos holds it in structure (the graduation rule):
// verified legal identity, gender identity, pronouns, preferred name and the
// postal address have, so they are here — the address as the current
// `member_addresses` row of each kind (ADR-130). SSN has not and stays
// backend2-sourced. Both ids are returned so Atlas can resolve either id,
// redirect to the canonical Galapagos-id URL, and fetch the backend2 fields by
// the sub.

// The subset sourced directly from `members` columns — tied to the table by the
// drift-guard test (response.drift.test.ts) so a column rename/removal can't
// silently drift the wire. The remaining owned fields (per-program clinical
// data, employer) are joined from sibling tables and are not column-backed.
export const MEMBER_READ_COLUMN_FIELDS = [
  'email',
  'phoneNumber',
  'status',
  'locale',
  'legacyBackend2Sub',
  'healthieUserId',
] as const;

// The subset sourced from `member_profiles` columns — same drift-guard
// treatment as MEMBER_READ_COLUMN_FIELDS. Every field is nullable on the wire
// regardless of column nullability because a member may have no profile row
// yet (the read projects the profile with left-join semantics).
export const MEMBER_PROFILE_COLUMN_FIELDS = [
  'legalFirstName',
  'legalLastName',
  'dateOfBirth',
  'sexAssignedAtBirth',
  'genderIdentity',
  'pronouns',
  'preferredName',
] as const;

// The profile-sourced fields whose wire key CANNOT equal the column name:
// `members.email` / `members.phone_number` already own the flat `email` /
// `phoneNumber` keys on this body, so the inverse-channel contact the
// registration flow stores on `member_profiles` is served under a prefix.
// Keyed wire-name -> column-name so the drift-guard still ties both
// directions — the values resolve against the live table, the keys against
// the hand-authored body — which a widened MEMBER_PROFILE_COLUMN_FIELDS
// could not do, since `profileEmail` is no column.
export const MEMBER_PROFILE_RENAMED_COLUMN_FIELDS = {
  profileEmail: 'email',
  profilePhoneNumber: 'phoneNumber',
} as const;

// The subset sourced directly from `member_addresses` columns. Unlike the two
// sets above it is tied to a DIFFERENT table than the rest of the body, so it
// gets its own drift-guard pairing (response.drift.test.ts) resolved against
// `memberAddresses` and asserted on the nested address object rather than the
// top-level body. Every entry is a real column name, so the derivation picks
// them straight off the table.
export const MEMBER_ADDRESS_COLUMN_FIELDS = [
  'id',
  'kind',
  'line1',
  'line2',
  'line3',
  'city',
  'state',
  'postalCode',
  'country',
  'createdAt',
] as const;

/**
 * One `member_addresses` row as staff read it (ADR-130). Deliberately NOT
 * `.strict()`: ADR-135 Rule 1's strict boundary nests, so a strict child would
 * break an installed client even under a lenient parent.
 *
 * `kind` rides the row rather than being dropped as redundant with the slot it
 * is served in. ADR-130 Rule 4 puts the shipping-falls-back-to-home decision on
 * the consumer, so the read has to be legible as NOT having made it: a
 * `shippingAddress` carrying `kind: 'shipping'` is the evidence no `home` row
 * was substituted.
 *
 * `id` is carried because ADR-130 Rule 3 has anything that ships or bills hold
 * the row id it shipped against, never a copy of the fields. `source` and
 * `sourceRef` are withheld: provenance is operator data, not care-staff data.
 * Nullability mirrors the columns exactly (ADR-117) — only `line2` and `line3`
 * are nullable there.
 */
export const memberAddressSchema = z.object({
  id: z.string(),
  // `z.string()`, not the `MEMBER_ADDRESS_KIND` enum, matching every other
  // CHECK-backed text value on this body (`status`, `careTrack`,
  // `carePathwayTier`, `eligibilityStatus`). A closed enum on a client-parsed
  // response makes a future vocabulary addition an unhandled enum value, which
  // ADR-135 Rule 3 classes as a BREAKING response change: an installed client
  // would reject the whole body on the first member carrying a new kind. The
  // vocabulary stays closed where it is enforceable — the DB CHECK and the
  // writer's own types — rather than at the boundary a client parses.
  kind: z.string(),
  line1: z.string(),
  line2: z.string().nullable(),
  line3: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  country: z.string(),
  createdAt: z.string(),
});
export type MemberAddress = z.infer<typeof memberAddressSchema>;

export const memberCompanySchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type MemberCompany = z.infer<typeof memberCompanySchema>;

// A member goal (≈ FHIR Goal). Includes `id` because goals are 1-many per
// program (no natural unique key on `metric_code`), so an edit must target a
// specific row. `targetValue` is served as a JS number (the `goals.target_value`
// numeric column, coerced at the boundary).
export const memberGoalSchema = z.object({
  id: z.string(),
  metricCode: z.string(),
  targetValue: z.number().nullable(),
  status: z.string(),
});
export type MemberGoal = z.infer<typeof memberGoalSchema>;

// One care-pathway tier change. `care_pathways` is append-only, so a program
// carries an ordered history (newest first) — the current tier is entry[0].
export const carePathwayHistoryEntrySchema = z.object({
  tier: z.string(),
  source: z.string(),
  effectiveAt: z.string().nullable(),
});
export type CarePathwayHistoryEntry = z.infer<typeof carePathwayHistoryEntrySchema>;

// One enrolled substance program's Galapagos-owned projection. Every field here
// is program-scoped in the schema (enrollment, care-pathway tier + history,
// eligibility, care track, subtype tags, goals all hang off a specific
// enrollment), so a multi-substance member carries a distinct set per program.
export const memberProgramSchema = z.object({
  // The enrollment's opaque id (`member_programs.id`). Exposed so a staff surface
  // can author check-in `entries` (which key on `memberProgramId`) for any enrolled
  // program — including one with no prior check-in that day.
  memberProgramId: z.string(),
  key: z.string(),
  isPrimary: z.boolean(),
  // Enrollment lifecycle (`member_programs`) — read-only here; enroll/unenroll
  // is a distinct clinical workflow, not an inline edit.
  enrolledAt: z.string().nullable(),
  status: z.string(),
  outcome: z.string(),
  endedAt: z.string().nullable(),
  careTrack: z.string().nullable(),
  // Clinical acuity tier (`care_pathways`) — Galapagos-owned, editable; the
  // full append-only history rides alongside the current value.
  carePathwayTier: z.string().nullable(),
  carePathwayHistory: z.array(carePathwayHistoryEntrySchema),
  // Eligibility (`member_eligibilities`) — synced read-model, read-only.
  eligibilityStatus: z.string().nullable(),
  eligibilityRelationship: z.string().nullable(),
  // Subtype tags (`member_program_tags`) — read-only (no tag catalog surfaced yet).
  subtypeTags: z.array(z.string()),
  goals: z.array(memberGoalSchema),
});
export type MemberProgram = z.infer<typeof memberProgramSchema>;

// The endpoint cannot leak an un-declared field because `sendOk` re-validates
// and sends `parsed.data`, which strips anything not declared here. Identity
// fields are member-level; every clinical/substance field is nested under its
// program.
// The latest parental-consent decision projected onto the member read. The
// stored recorded_by (staff identity) is audit data and deliberately NOT on
// the wire; null = no row = the surface renders Unknown.
//
// NOT `.strict()`, for the same reason its parent is not: the strict boundary
// NESTS, so a strict child breaks an already-loaded Atlas bundle exactly as a
// strict top level does, one field down (ADR-135 Rule 1). Relaxing only the
// parent would have left the mechanism in place here. The fail-closed property
// is unaffected — `sendOk` sends `parsed.data`, so an un-declared field is
// stripped before the wire rather than rejected at it.
export const memberParentalConsentSchema = z.object({
  granted: z.boolean(),
  version: z.number().int().positive(),
  recordedAt: z.string(),
  source: z.string(),
});
export type MemberParentalConsent = z.infer<typeof memberParentalConsentSchema>;

// Latest staff flip of the member-app treatment-plan offer gate — null until
// the first row exists (Off by default). `recorded_by` stays server-side only.
export const memberTreatmentPlanOfferSchema = z.object({
  enabled: z.boolean(),
  recordedAt: z.string(),
  source: z.string(),
});
export type MemberTreatmentPlanOffer = z.infer<typeof memberTreatmentPlanOfferSchema>;

// One unsuccessful attempt to reach the member about the escalation: when, and
// who tried. The attempt's working note is deliberately absent — it stays in
// the access-controlled store and reaches no response. `attemptedByName` is the
// server-resolved display name, so a client never has to resolve staff ids.
export const activeEscalationContactAttemptSchema = z.object({
  attemptedAt: z.string(),
  attemptedByName: z.string(),
});
export type ActiveEscalationContactAttempt = z.infer<typeof activeEscalationContactAttemptSchema>;

// The member's active safety escalation, projected onto the member-detail read
// so the staff surface can raise its banner without a second round trip.
//
// The field set is deliberately minimal and deliberately EXHAUSTIVE: these six
// are what the banner and its evidence modal need, and nothing else from the
// row travels. In particular `escalations.reason` — unbounded model-authored
// free text — is persisted inside the access-controlled store and rendered
// nowhere. The evidence surfaces are the conversation transcript (agent source)
// and the assessment band (assessment source), so no reader needs it.
//
// `conversationId` is the evidence pointer and is nullable twice over: an
// assessment-sourced escalation never had one, and the column is `set null` on
// conversation deletion, so an agent-sourced escalation can lose it too.
// It is the INTERNAL `conversations.id`, which the transcript route does not
// accept — that route looks a conversation up by its provider's id.
//
// `transcriptConversationId` is the id the transcript route DOES accept: the
// conversation's provider (`elevenlabs`) external id, or null when the
// escalation has no conversation or the conversation has no such source.
// Optional as well as nullable because an Atlas bundle can reach production
// before the API that sends it, and a required key absent from that older
// response would fail the whole member-detail parse. A reader treats absent
// exactly like null.
//
// `source` is the WIRE vocabulary, not the stored one: a client bundle parses
// this response at runtime, so a stored source it has never seen must not
// arrive here (ADR-135 Rule 3). The service maps through
// `toActiveEscalationSource`.
//
// `intakeWithdrawalCheck` is how the manual-path withdrawal check travels
// without widening `source`: an optional flag an older bundle ignores (it then
// shows the assessment wording), present and true only for that stored source.
//
// NOT `.strict()`, and neither is its parent — see the note on
// `memberReadDataSchema`.
export const activeEscalationSchema = z.object({
  id: z.string(),
  source: activeEscalationSourceSchema,
  intakeWithdrawalCheck: z.boolean().optional(),
  raisedAt: z.string(),
  status: escalationStatusSchema,
  conversationId: z.string().nullable(),
  transcriptConversationId: z.string().nullable().optional(),
  // The stored message position of the transcript turn to mark as the signal,
  // matched against `position` on the transcript read's turns; null when the
  // escalation names no turn. Optional because an Atlas bundle that ships ahead
  // of the API reads a response without it, and must render no marker rather
  // than fail the member page.
  signalTurnPosition: z.number().int().nonnegative().nullable().optional(),
  // The unsuccessful attempts to reach the member about THIS escalation: the
  // true total, the newest attempt's instant, and the newest attempts (newest
  // first, bounded). They ride the write echo too, which is what lets a client
  // show its own logged attempt without a second read.
  //
  // Added in place on a response Atlas alone parses, and parses non-strictly,
  // so a bundle that predates them strips the keys (ADR-135 Rule 2 (a); the
  // residual risk from a tab older than the strict-parser removal is accepted
  // under (b), as it already is for the two fields above). Optional so a NEW
  // bundle reading an API that predates them renders "no attempts" rather than
  // failing the member page (Rule 6). A reader treats absent as zero.
  contactAttemptCount: z.number().int().nonnegative().optional(),
  lastContactAttemptAt: z.string().nullable().optional(),
  // `.max()` binds every loaded Atlas bundle to today's bound: raising the
  // list limit later makes a stale bundle's parse throw on the member page, so
  // it is a breaking response change under ADR-135 Rule 3, not a tuning knob.
  contactAttempts: z
    .array(activeEscalationContactAttemptSchema)
    .max(ESCALATION_CONTACT_ATTEMPTS_LIST_LIMIT)
    .optional(),
  // Who holds this escalation ("In progress"), or null when it is free. It
  // rides the write echo too, so a write elsewhere on the member page never
  // shows a claimed escalation as free. `heldByYou` is computed for the staff
  // member who asked, never sent as an id.
  //
  // Added in place, and this change explicitly accepts the residual ADR-135
  // Rule 2(b) risk from an Atlas tab older than the strict-parser removal, as
  // the contact-attempt and transcript fields above already did: such a tab
  // fails the member page until it reloads. Optional so a new bundle reading
  // an API that predates the field renders "Open" (Rule 6).
  claim: escalationClaimSchema.nullable().optional(),
});
export type ActiveEscalation = z.infer<typeof activeEscalationSchema>;

// The active escalation as the member-detail GET read carries it: the base
// shape plus what the member reported on the intake withdrawal check, in form
// order, present only for that source and only when the stored answer holds a
// known symptom. GET only: the write echoes keep the base shape (see
// `memberMutationReadDataSchema`), so a write never carries these PHI codes or
// pays for the lookup behind them.
//
// Optional so a new Atlas bundle reading an API that predates the field renders
// its no-symptoms fallback rather than failing the member page (ADR-135 Rule 6).
// `.max()` is the vocabulary size, which no de-duplicated reading exceeds.
export const activeEscalationReadSchema = activeEscalationSchema.extend({
  intakeWithdrawalSymptoms: z
    .array(reportedWithdrawalSymptomSchema)
    .max(REPORTED_WITHDRAWAL_SYMPTOM.length)
    .optional(),
});
export type ActiveEscalationRead = z.infer<typeof activeEscalationReadSchema>;

/** Member zone as staff may read it on a member detail response (ADR-095 Rule 9). */
export const memberResolvedTimezoneSchema = z.discriminatedUnion('known', [
  z.object({ known: z.literal(true), zone: ianaTimezoneSchema }),
  z.object({ known: z.literal(false), zone: ianaTimezoneSchema }),
]);
export type MemberResolvedTimezone = z.infer<typeof memberResolvedTimezoneSchema>;

export const memberReadDataSchema = z.object({
  memberId: z.string(),
  legacyBackend2Sub: z.string().nullable(),
  // Identity (`members`, mirrored from Cognito per ADR-028) — read-only here.
  email: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  status: z.string(),
  locale: z.string().nullable(),
  termsAcceptedAt: z.string().nullable(),
  hipaaConsentAt: z.string().nullable(),
  createdAt: z.string().nullable(),
  // The Healthie chart this member's documents file into (`members`
  // column) — null until the link consumer or a staff action sets it. Staff
  // projection only; the member-facing reads never carry a vendor chart id.
  healthieUserId: z.string().nullable(),
  // Verified legal identity (`member_profiles`, written by the eligibility
  // verifier on a matched resolution) — read-only here; null until verified.
  legalFirstName: z.string().nullable(),
  legalLastName: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  // Identity details in the member's own words (`member_profiles`, open
  // strings bounded by the same constant as the column CHECK) — read-only
  // here; null until the legacy import or a later edit surface writes them.
  // Defaulted like `parentalConsent` / `isMinor` below so a producer or
  // fixture that omits the key parses as null. The wire still rejects a
  // string over the bound.
  genderIdentity: z.string().max(MEMBER_PROFILE_IDENTITY_TEXT_MAX).nullable().default(null),
  pronouns: z.string().max(MEMBER_PROFILE_IDENTITY_TEXT_MAX).nullable().default(null),
  preferredName: z.string().max(MEMBER_PROFILE_IDENTITY_TEXT_MAX).nullable().default(null),
  // Sex assigned at birth (`member_profiles`, written by the registration
  // flow) — an administrative/clinical fact, NOT the identity details above,
  // which are how the member wishes to be addressed. Read-only here.
  //
  // A plain string rather than the closed `SEX_ASSIGNED_AT_BIRTH` enum the
  // column CHECK and the registration write schema both enforce. A zod enum
  // THROWS on a member it does not know, and this body is parsed at runtime by
  // Atlas bundles that can be older than the server — so widening the
  // vocabulary later would fail the whole member page rather than one row
  // (ADR-135 Rule 3). The vocabulary stays enforced where a violation is
  // actually preventable: at the write and in the database.
  //
  // Defaulted like its neighbours so a producer or fixture that omits the key
  // parses as null; a bare `.nullable()` still requires the key to be present.
  sexAssignedAtBirth: z.string().nullable().default(null),
  // The member's OTHER contact channel (`member_profiles`, collected during
  // registration): the inverse of whichever channel they authenticate with,
  // so it is the route to try when `email` / `phoneNumber` above fail. Never
  // a fallback copy of those — a null here means no second channel is on
  // file, not that the primary one should be read instead.
  profileEmail: z.string().nullable(),
  profilePhoneNumber: z.string().nullable(),
  // Sponsor/employer is member-level, sourced from the member's eligibility
  // grant's provider-qualified `client_ref` (both fields carry the ref — no
  // local name source) — read-only. Displayed once, not per program.
  company: memberCompanySchema.nullable(),
  // The member's CURRENT address of each kind — the newest `member_addresses`
  // row per (member, kind), read through `MemberAddressesRepo.current()`
  // (ADR-130 Rule 2's single definition of "current"). Null means the member
  // has no row of that kind: a migrated member whose backend2 addresses are
  // not imported yet, or one whose legacy address was too partial for the
  // table's NOT NULL columns to admit (ADR-130 Rule 9). It never means "read
  // the other kind instead" — a fulfilment path wanting a shipping address
  // falls back to `homeAddress` itself (ADR-130 Rule 4).
  homeAddress: memberAddressSchema.nullable().default(null),
  shippingAddress: memberAddressSchema.nullable().default(null),
  // Care-team-set follow-up date (`member_next_contact_due_dates`) — member-level
  // like the identity fields above, not program-scoped. Editable via the
  // next-contact-due-date PATCH route.
  nextContactDueDate: z.string().nullable(),
  // Latest parental-consent decision (`member_consents`, append-only) — null
  // until staff record one. Editable via the parental-consent PATCH route.
  parentalConsent: memberParentalConsentSchema.nullable().default(null),
  // Latest treatment-plan offer flip (`member_treatment_plan_offers`) — null
  // means Off. Editable via the treatment-plan-offer PATCH route.
  treatmentPlanOffer: memberTreatmentPlanOfferSchema.nullable().default(null),
  // Server-derived minor display hint (live age vs state age of majority);
  // null without a verified DOB. Clients never compute age.
  isMinor: z.boolean().nullable().default(null),
  // Server-derived staff display rule: under 18 today in the member-local day,
  // in every state (unlike `isMinor`, which follows the state age of majority
  // and drives escalation handling). Null without a parseable DOB. Defaulted
  // because a server that predates the field omits it.
  isUnder18: z.boolean().nullable().default(null),
  // The ADR-122 hold condition: enrolled as a minor (`was_minor_at_enrolment`)
  // with no granted parental consent. Stays true after the member turns 18, so
  // staff can still reach the consent control to release the hold. Defaulted
  // because a server that predates the field omits it.
  consentPending: z.boolean().default(false),
  // Enrolled substance programs, main program first (`isPrimary`).
  programs: z.array(memberProgramSchema),
  // True when any enrolled programme has `care.medication_eligible` — staff
  // may offer a provider call only when this is true.
  medicationEligible: z.boolean(),
  // The member's active (unhandled) safety escalation, or null when there is
  // none. At most one — the banner shows the current signal, not a history.
  activeEscalation: activeEscalationReadSchema.nullable(),
  // Resolved through the single member-timezone path; null when omitted by an
  // older API (ADR-135). `known: false` means the default zone applies — staff
  // must not treat the zone name as the member's.
  timezone: memberResolvedTimezoneSchema.nullable().default(null),
  // How the member wants first contact (call or chat) and the language their
  // care calls need, from `member_care_preferences`; null when the member has
  // never saved one. Validated against the vocabularies here; Atlas reads it
  // through the open-string client override below.
  carePreferences: memberCarePreferencesSummarySchema.nullable().default(null),
  // Latest registration acuity for the main programme — null when never assessed.
  registrationAcuity: registrationAcuitySchema.nullable().optional(),
});
// Deliberately NOT `.strict()`: Atlas parses this schema at runtime through
// `HttpClient.get`, which calls `schema.parse()` and THROWS on an unknown key
// rather than stripping it. Under `.strict()` a bundle loaded before a deploy
// that adds a field fails the entire member page, not just the new surface —
// so strictness here is a client-breakage mechanism, not a safety one
// (ADR-135 Rule 1). Zod strips unknown keys by default, which is what a
// response a client parses should do.
//
// The fail-closed property strictness was carrying survives, because `sendOk`
// sends `parsed.data` rather than the object handed to it: a column that is
// not declared here still never reaches the wire, it is simply dropped rather
// than turning the endpoint into a 500.
//
// This is NOT about older clients keeping up with new fields — that reasoning
// is inverted (ADR-135 Rule 6). It is about this server's responses staying
// parseable by bundles that are already out there.
export type MemberReadData = z.infer<typeof memberReadDataSchema>;

export const memberReadResponseSchema = apiEnvelope(memberReadDataSchema);
export type MemberReadResponse = z.infer<typeof memberReadResponseSchema>;

const memberReadTimezoneWireSchema = z.discriminatedUnion('known', [
  z.object({ known: z.literal(true), zone: z.string().min(1).max(64) }),
  z.object({ known: z.literal(false), zone: z.string().min(1).max(64) }),
]);

/** Client parse for staff member read — timezone is wire-structural; unusable zones degrade in UI (ADR-135). */
export const memberReadDataClientSchema = memberReadDataSchema
  .omit({ timezone: true, carePreferences: true })
  .extend({
    timezone: memberReadTimezoneWireSchema.nullable().default(null),
    // Open strings, so a vocabulary that grows after this bundle shipped shows
    // the card's fallback text instead of failing the member page (ADR-135).
    carePreferences: memberCarePreferencesSummaryClientSchema.nullable().default(null),
  });
export type MemberReadDataClient = z.infer<typeof memberReadDataClientSchema>;
export const memberReadResponseClientSchema = apiEnvelope(memberReadDataClientSchema);
export type MemberReadResponseClient = z.infer<typeof memberReadResponseClientSchema>;

// Write routes re-read the member and return it through THIS body, not the
// read projection above. A field that graduates onto the GET read stays off
// the write echo: a client bundle whose write-response parser predates it
// (ADR-135 — a tab left open across the deploy) must never receive a 200 it
// cannot parse for a mutation it has already applied, because a
// parental-consent or tier append retried on that "failure" lands twice. The
// GET read is the one place a stale parser can fail safely (a retryable read),
// so every newly-graduated field lives there and nowhere a write echoes it.
//
// That is why the postal address is omitted here too, and not only the
// identity details this split was introduced for. The rule is the shape of the
// change — an additive key on a client-parsed body — not which slice added it,
// so a field arriving later inherits the same treatment rather than needing
// the hazard rediscovered.
export const MEMBER_MUTATION_READ_OMITTED_FIELDS = [
  'genderIdentity',
  'pronouns',
  'preferredName',
  'sexAssignedAtBirth',
  'homeAddress',
  'shippingAddress',
  'treatmentPlanOffer',
  'timezone',
  'carePreferences',
] as const;

export const memberMutationReadDataSchema = memberReadDataSchema
  .omit({
    genderIdentity: true,
    pronouns: true,
    preferredName: true,
    sexAssignedAtBirth: true,
    homeAddress: true,
    shippingAddress: true,
    treatmentPlanOffer: true,
    timezone: true,
    carePreferences: true,
  })
  .extend({
    // The base escalation shape: the GET-only fields on `activeEscalationReadSchema`
    // stay off the write echo for the same reason the omitted fields above do.
    activeEscalation: activeEscalationSchema.nullable(),
  });
export type MemberMutationReadData = z.infer<typeof memberMutationReadDataSchema>;
export const memberMutationReadResponseSchema = apiEnvelope(memberMutationReadDataSchema);
export type MemberMutationReadResponse = z.infer<typeof memberMutationReadResponseSchema>;

/** The write-route echo of a member: the read projection minus every graduated field. */
export function toMemberMutationReadData(data: MemberReadData): MemberMutationReadData {
  const {
    genderIdentity,
    pronouns,
    preferredName,
    sexAssignedAtBirth,
    homeAddress,
    shippingAddress,
    treatmentPlanOffer,
    timezone,
    carePreferences,
    activeEscalation,
    ...rest
  } = data;
  void genderIdentity;
  void pronouns;
  void preferredName;
  void sexAssignedAtBirth;
  void homeAddress;
  void timezone;
  void shippingAddress;
  void treatmentPlanOffer;
  void carePreferences;
  return { ...rest, activeEscalation: toMutationActiveEscalation(activeEscalation) };
}

/** The GET read's active escalation without its GET-only fields. */
function toMutationActiveEscalation(
  escalation: MemberReadData['activeEscalation'],
): MemberMutationReadData['activeEscalation'] {
  if (escalation === null) return null;
  const { intakeWithdrawalSymptoms, ...base } = escalation;
  void intakeWithdrawalSymptoms;
  return base;
}

/** Treatment-plan-offer PATCH echoes the mutation body plus the flipped field. */
export const treatmentPlanOfferWriteDataSchema = memberMutationReadDataSchema.extend({
  treatmentPlanOffer: memberReadDataSchema.shape.treatmentPlanOffer,
});
export type TreatmentPlanOfferWriteData = z.infer<typeof treatmentPlanOfferWriteDataSchema>;

export function toTreatmentPlanOfferWriteData(data: MemberReadData): TreatmentPlanOfferWriteData {
  return {
    ...toMemberMutationReadData(data),
    treatmentPlanOffer: data.treatmentPlanOffer,
  };
}

// The member-LIST row is the Galapagos-owned identity-only projection (no
// demographics/PII — none exist on `members`). The subset sourced directly from
// `members` columns is tied to the table by the drift-guard (response.drift.test.ts)
// so a column rename/removal can't silently drift the wire. `memberId` maps to
// the renamed `members.id` column, so — like MEMBER_READ_COLUMN_FIELDS — it is
// not in the column-named set below.
export const MEMBER_LIST_COLUMN_FIELDS = [
  'email',
  'phoneNumber',
  'status',
  'legacyBackend2Sub',
  'createdAt',
] as const;

// The caseload fields, sourced 1:1 from `member_list_projections` columns and
// tied to that table by the same drift-guard. `programs` is shaped from two
// columns (`active_program_keys` + `main_program_key`) so it is not in this set,
// exactly as `memberId` is absent from the members-column set above.
export const MEMBER_LIST_PROJECTION_FIELDS = [
  'legalFirstName',
  'legalLastName',
  'lastCheckInAt',
  'lastCheckInMoods',
  'lastSeenAt',
  'nextCallAt',
  'nextCallProvider',
  'onboardingCompletedAt',
  'careInterest',
  'nextContactDueDate',
  'needsContactSince',
  'needsContactReason',
  'healthieUserId',
] as const;

// One active enrollment as the list renders it: the catalog key plus whether it
// is the member's main programme. Deliberately minimal — the list draws an icon
// and a tooltip, not the per-programme clinical detail the detail page carries.
export const memberListProgramSchema = z
  .object({
    key: z.string(),
    isPrimary: z.boolean(),
  })
  .strict();
export type MemberListProgram = z.infer<typeof memberListProgramSchema>;

// Strict so the list endpoint can never leak an un-declared field (fail-closed);
// sendOk re-validates before sending. Every field is one source fact: there is
// deliberately no composite/ranked status here — a precedence order over these
// facts would be this codebase's invention rather than a property of the data,
// and it would hide the axes the care person filters and sorts on.
export const memberListItemSchema = z
  .object({
    memberId: z.string(),
    email: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    status: z.string(),
    legacyBackend2Sub: z.string().nullable(),
    // The member's Healthie client id, read from the projection's copy — what the
    // list's chart link is built from. Null when no chart is linked.
    healthieUserId: z.string().nullable(),
    // `members.created_at` is NOT NULL (ADR-056), so the keyset timestamp is
    // always present — non-nullable keeps the projection table-traceable.
    createdAt: z.string(),
    // Verified legal identity, copied onto the projection from `member_profiles`.
    // Both nullable and independent of each other: a member whose eligibility has
    // not matched has no profile row, so the list falls back to its email-derived
    // label rather than rendering blank. Date of birth is absent by design — it is
    // shown only on the member-detail card.
    legalFirstName: z.string().nullable(),
    legalLastName: z.string().nullable(),
    // Active enrollments, main programme first.
    programs: z.array(memberListProgramSchema),
    lastCheckInAt: z.string().nullable(),
    // The moods of that check-in, in selection order; empty when none recorded.
    lastCheckInMoods: z.array(z.string()),
    // The later of a session-active or check-in event — never earlier than
    // lastCheckInAt (a check-in counts as being seen too). The list's primary
    // sort/display column; lastCheckInAt is an independent secondary fact.
    lastSeenAt: z.string().nullable(),
    nextCallAt: z.string().nullable(),
    nextCallProvider: z.string().nullable(),
    onboardingCompletedAt: z.string().nullable(),
    // Tri-state: never asked (null), interested, or declined.
    careInterest: z.boolean().nullable(),
    // Care-team-set follow-up date — member-owned, independent of any booking.
    nextContactDueDate: z.string().nullable(),
    // The open contact case: when the member started waiting, and what for.
    // Null together when no case is open — a state, not missing data.
    //
    // `needsContactReason` is a bare string rather than the opened-reason enum
    // on purpose. The list renders it and takes no decision from it, so pinning
    // the vocabulary here would buy nothing and would make a client reject the
    // whole response the day a new reason is added server-side (ADR-135 Rule 3).
    // `status` and the programme keys are carried the same way for the same
    // reason.
    needsContactSince: z.string().nullable(),
    needsContactReason: z.string().nullable(),
  })
  .strict();
export type MemberListItem = z.infer<typeof memberListItemSchema>;

// Keyset-paginated list body: `nextCursor` is null on the last page.
export const memberListDataSchema = z
  .object({
    items: z.array(memberListItemSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type MemberListData = z.infer<typeof memberListDataSchema>;

export const memberListResponseSchema = apiEnvelope(memberListDataSchema);
export type MemberListResponse = z.infer<typeof memberListResponseSchema>;

// Member account status — declared in ./status so the persistence layer can
// derive its CHECK from the same tuple without importing this wire module.
// Re-exported here so every existing consumer keeps its import path.
export { MEMBER_STATUS, memberStatusSchema, type MemberStatus } from './status';
// The statuses the list will ever return. `deleted` is deliberately absent: on a
// clinical console a deleted member is the record someone asked to have removed,
// so it must not be reachable by browsing or by hand-crafting a query param. The
// repo applies the exclusion unconditionally; this tuple is what the UI offers
// and what a caller-supplied filter is intersected with.
export const MEMBER_LIST_LISTABLE_STATUS = ['active', 'disabled'] as const;
export type MemberListListableStatus = (typeof MEMBER_LIST_LISTABLE_STATUS)[number];

// The sortable keyset columns; direction toggles the keyset so paging stays
// stable either way. Each one needs its own partial btree on the projection, so
// adding a member here is a deliberate pair of changes, not a free switch.
// `lastSeenAt` replaces `lastCheckInAt` as the list's primary sort (Pelago 1.0
// parity) — `lastCheckInAt` is no longer independently sortable, symmetric with
// how `lastSeenAt` was not sortable before this change.
export const MEMBER_LIST_SORT = [
  'createdAt',
  'lastSeenAt',
  'nextCallAt',
  'nextContactDueDate',
  // Who has waited longest. Pairs with the `needsContact` filter: ascending is
  // longest-waiting-first, which is only the intended reading once the members
  // with no open case are filtered out (they sort first, ASC NULLS FIRST, like
  // every other nullable column here).
  'needsContactSince',
] as const;
export const memberListSortSchema = z.enum(MEMBER_LIST_SORT);
export type MemberListSort = (typeof MEMBER_LIST_SORT)[number];
export const MEMBER_LIST_ORDER = ['asc', 'desc'] as const;
export const memberListOrderSchema = z.enum(MEMBER_LIST_ORDER);
export type MemberListOrder = (typeof MEMBER_LIST_ORDER)[number];

/**
 * The "material gap" threshold between `lastSeenAt` and `lastCheckInAt` used
 * by the `seenWithoutCheckIn` filter: a member with no check-in at all while
 * seen, or a gap exceeding this many days, counts as "present but not
 * checking in." Shared so the filter has exactly one number to change.
 */
export const MEMBER_LIST_SEEN_GAP_DAYS = 7;

/**
 * The shortest search a trigram index can serve: a pattern under three characters
 * produces no trigrams, so the index cannot be used and Postgres would fall back
 * to a sequential scan.
 *
 * It lives in the shared contract because BOTH sides need the same number — the
 * repo enforces it (returning empty rather than scanning on a keystroke) and the
 * UI states it, so a short query reads as "type one more character" instead of
 * "no such members". Two copies of the number would drift.
 */
export const MEMBER_LIST_MIN_SEARCH_LENGTH = 3;

// Check-in recency buckets the care person can filter by. These are filter
// OPTIONS the reader chooses, not a verdict this code reaches about a member —
// which is why the boundaries live here rather than being folded into a stored
// "engagement" grade. `never` selects members who have never checked in.
export const MEMBER_LIST_CHECK_IN_BUCKET = ['7d', '14d', '30d', 'over30d', 'never'] as const;
export const memberListCheckInBucketSchema = z.enum(MEMBER_LIST_CHECK_IN_BUCKET);
export type MemberListCheckInBucket = (typeof MEMBER_LIST_CHECK_IN_BUCKET)[number];

// The list/search query params (wire shape — optional). `q` matches email /
// phone / members.id / legacy_backend2_sub; `limit`/`cursor` drive the keyset
// page; `status` filters by account status; `sort`+`order` pick the keyset
// direction (default createdAt desc). The client builds request params from this;
// the handler validates + clamps/decodes server-side (an out-of-enum status/sort
// is a 400).
export const memberListQuerySchema = z
  .object({
    q: z.string().optional(),
    limit: z.string().optional(),
    cursor: z.string().optional(),
    // Multi-select status filter, wired as a comma-separated list (e.g.
    // `active,disabled`); the values are validated with `memberListStatusFilterSchema`.
    status: z.string().optional(),
    // Multi-select programme filter, same CSV convention as `status`; validated
    // with `memberListProgramFilterSchema`. Semantics are OR within the filter
    // (a member active in ANY selected programme) and AND against every other.
    program: z.string().optional(),
    // Each of the three below is an independent axis rather than a facet of one
    // synthesized status, because "who have I not booked" and "who has stopped
    // checking in" are different questions asked by different people.
    onboarded: z.enum(['true', 'false']).optional(),
    callBooked: z.enum(['true', 'false']).optional(),
    checkIn: memberListCheckInBucketSchema.optional(),
    // Present but not checking in: no check-in at all while seen, or a gap
    // exceeding MEMBER_LIST_SEEN_GAP_DAYS. Mutually exclusive with
    // `neverSeen` by construction — lastSeenAt must be present for this to
    // match, absent for that one.
    seenWithoutCheckIn: z.enum(['true', 'false']).optional(),
    // No signal of any kind — lastSeenAt IS NULL. A pure null check, not a
    // recency bucket, so it needs no threshold.
    neverSeen: z.enum(['true', 'false']).optional(),
    // Whether the member has an open contact case — an axis of its own, beside
    // the four above, never a facet of a synthesized status. Binary on purpose:
    // "is this member waiting" is the filter's question, and "how long" is the
    // sort's, so there is no bucket enum here.
    needsContact: z.enum(['true', 'false']).optional(),
    sort: memberListSortSchema.optional(),
    order: memberListOrderSchema.optional(),
  })
  .strict();
export type MemberListQuery = z.infer<typeof memberListQuerySchema>;

/**
 * The parsed status filter: an array of member statuses. `status` on the wire is
 * a CSV string; the server splits it and validates each value against this
 * schema (the single source of truth for the allowed filter values).
 */
export const memberListStatusFilterSchema = z.array(z.enum(MEMBER_STATUS));
export type MemberListStatusFilter = z.infer<typeof memberListStatusFilterSchema>;

/**
 * The parsed programme filter: catalog keys. `program` on the wire is a CSV
 * string; the server splits it and validates each value against the programme
 * catalog, so an unknown key is a 400 rather than a silently-empty result.
 */
export const memberListProgramFilterSchema = z.array(z.string().min(1));
export type MemberListProgramFilter = z.infer<typeof memberListProgramFilterSchema>;

// Every write below returns the re-read projection (read-your-writes) so the
// client updates without a second round-trip; none ever touches backend2.

// Care-pathway tier: append a new `care_pathways` row (append-only history).
export const carePathwayTierWriteRequestSchema = z
  .object({
    tier: z.enum(CARE_PATHWAY_TIER),
    // Which enrolled program the tier applies to. Omitted → the primary program.
    programKey: z.string().optional(),
    // Why the clinician changed the tier; stored on the row, never published.
    reason: z.string().max(500).optional(),
  })
  .strict();
export type CarePathwayTierWriteRequest = z.infer<typeof carePathwayTierWriteRequestSchema>;

export const carePathwayTierWriteResponseSchema = memberMutationReadResponseSchema;
export type CarePathwayTierWriteResponse = z.infer<typeof carePathwayTierWriteResponseSchema>;

// Goal: update a specific goal row (`goals`, keyed by id in the path). Both
// fields optional; at least one must be present.
export const goalUpdateRequestSchema = z
  .object({
    status: z.enum(GOAL_STATUS).optional(),
    targetValue: z.number().nullable().optional(),
  })
  .strict()
  .refine((v) => v.status !== undefined || v.targetValue !== undefined, {
    message: 'at least one of status or targetValue is required',
  });
export type GoalUpdateRequest = z.infer<typeof goalUpdateRequestSchema>;

export const goalUpdateResponseSchema = memberMutationReadResponseSchema;
export type GoalUpdateResponse = z.infer<typeof goalUpdateResponseSchema>;

// Care track: update `care_plans.care_track` (the delivery mode) for a program.
export const parentalConsentWriteRequestSchema = z
  .object({
    granted: z.boolean(),
    change: memberDetailChangeSchema,
  })
  .strict();
export type ParentalConsentWriteRequest = z.infer<typeof parentalConsentWriteRequestSchema>;

export const parentalConsentWriteResponseSchema = memberMutationReadResponseSchema;
export type ParentalConsentWriteResponse = z.infer<typeof parentalConsentWriteResponseSchema>;

export const treatmentPlanOfferWriteRequestSchema = z
  .object({
    enabled: z.boolean(),
  })
  .strict();
export type TreatmentPlanOfferWriteRequest = z.infer<typeof treatmentPlanOfferWriteRequestSchema>;

export const treatmentPlanOfferWriteResponseSchema = apiEnvelope(treatmentPlanOfferWriteDataSchema);
export type TreatmentPlanOfferWriteResponse = z.infer<typeof treatmentPlanOfferWriteResponseSchema>;

export const careTrackWriteRequestSchema = z
  .object({
    careTrack: z.enum(CARE_TRACK),
    programKey: z.string(),
  })
  .strict();
export type CareTrackWriteRequest = z.infer<typeof careTrackWriteRequestSchema>;

export const careTrackWriteResponseSchema = memberMutationReadResponseSchema;
export type CareTrackWriteResponse = z.infer<typeof careTrackWriteResponseSchema>;

const programmeEndDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'endedAt must be YYYY-MM-DD');

export const programmeEndWriteRequestSchema = z
  .object({
    programKey: z.string().min(1),
    reason: programEndReasonSchema,
    note: z.string().max(PROGRAM_END_NOTE_MAX_LENGTH).optional(),
    endedAt: programmeEndDateSchema.optional(),
    change: memberDetailChangeSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.reason === 'other' && (!value.note || !value.note.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'note is required when reason is other',
        path: ['note'],
      });
    }
  });
export type ProgrammeEndWriteRequest = z.infer<typeof programmeEndWriteRequestSchema>;

export const programmeEndWriteResponseSchema = memberMutationReadResponseSchema;
export type ProgrammeEndWriteResponse = z.infer<typeof programmeEndWriteResponseSchema>;

// Staff-facing read of a member's upcoming Galapagos care appointments
// (`GET /api/v1/internal/members/{ref}/care-appointments`) — the member-page
// counterpart of the specialist-scoped telemedicine list. Specialist identity
// is the member-facing display name only, never staff account detail.
//
// NOT `.strict()` — Atlas parses this through `galapagosHttpClient.get`, which
// throws on unknown keys under strict mode (ADR-135 Rule 1). `callType` is an
// additive field on this existing route; see the spec carve-out for residual
// pre-tolerance risk on pre-tolerance Atlas bundles.
export const memberCareAppointmentSchema = z.object({
  id: z.string().uuid(),
  startsAt: z.string().datetime({ offset: true }),
  durationMinutes: z.number().int().positive(),
  status: z.literal('booked'),
  specialistId: z.string().uuid(),
  specialistName: z.string(),
  isSelf: z.boolean(),
  /** Present when the server knows the booked call type; may be absent on
   * older rows or reads that omit it. */
  callType: routableCareCallTypeSchema.optional(),
  /** Present when the booking carries a member-ops or related reason; omitted when general. */
  callReason: tolerantCareCallReasonSchema,
  /** The raw Pelago 1.0 `reason` token, on imported rows only. */
  legacyReason: z.string().nullable().optional(),
  /** Populated when attendance was recorded while the appointment stays booked. */
  attendance: careAppointmentAttendanceSchema.nullable().optional(),
  needsAttendance: z.boolean().optional(),
  /** Visit modality when stored; omitted on legacy-import null rows (ADR-135). */
  modality: careAppointmentModalitySchema.optional(),
  /** False when the member may not cancel this visit themselves (in person). */
  memberCanCancel: z.boolean().optional(),
  /** False when stored modality is outside the call type's allowed set. */
  modalityAllowed: z.boolean().optional(),
});
export type MemberCareAppointment = z.infer<typeof memberCareAppointmentSchema>;

export const memberCareAppointmentsDataSchema = z.object({
  appointments: z.array(memberCareAppointmentSchema),
});
export type MemberCareAppointmentsData = z.infer<typeof memberCareAppointmentsDataSchema>;

export const memberCareAppointmentsResponseSchema = apiEnvelope(memberCareAppointmentsDataSchema);
export type MemberCareAppointmentsResponse = z.infer<typeof memberCareAppointmentsResponseSchema>;

// Staff-facing read of a member's consent history
// (`GET /api/v1/internal/members/{ref}/consents`): every `member_consents` row,
// each with its rendered document when one exists. `recorded_by` and the
// `evidence` jsonb are audit data and deliberately NOT on the wire; the
// document carries the stamped signer name and time because the consent row
// is written before the member's name exists. Bytes are never inlined — the
// download-url route mints a short-lived presigned GET per request.
/** `member_documents` columns served on the consent-document wire object (drift-guarded against the table). */
export const MEMBER_CONSENT_DOCUMENT_COLUMN_FIELDS = [
  'id',
  'signerName',
  'signedAt',
  'renderedAt',
  'displayName',
  'filedAt',
  'healthieDocumentId',
] as const;

// The four consent/document response schemas below are client-parsed (Atlas
// consents card and document download): they strip unknown keys rather than
// reject them, so an additive server field never breaks an installed bundle.
export const memberConsentDocumentSchema = z.object({
  id: z.string().uuid(),
  signerName: z.string(),
  signedAt: z.string().datetime({ offset: true }),
  renderedAt: z.string().datetime({ offset: true }),
  displayName: z.string(),
  filedAt: z.string().datetime({ offset: true }).nullable(),
  healthieDocumentId: z.string().nullable(),
});
export type MemberConsentDocument = z.infer<typeof memberConsentDocumentSchema>;

/**
 * `member_consents` columns served on the consent-record wire object; the row's
 * `createdAt` is served as `recordedAt` (the signing instant), so it is tied to
 * the table separately in the drift guard.
 */
export const MEMBER_CONSENT_RECORD_COLUMN_FIELDS = [
  'id',
  'consentType',
  'version',
  'granted',
  'method',
  'documentVersion',
  'source',
] as const;

export const memberConsentRecordSchema = z.object({
  id: z.string().uuid(),
  consentType: z.string(),
  version: z.number().int().positive(),
  granted: z.boolean(),
  method: z.string(),
  documentVersion: z.string().nullable(),
  recordedAt: z.string().datetime({ offset: true }),
  source: z.string(),
  document: memberConsentDocumentSchema.nullable(),
});
export type MemberConsentRecord = z.infer<typeof memberConsentRecordSchema>;

export const memberConsentsDataSchema = z.object({
  consents: z.array(memberConsentRecordSchema),
});
export type MemberConsentsData = z.infer<typeof memberConsentsDataSchema>;

export const memberConsentsResponseSchema = apiEnvelope(memberConsentsDataSchema);
export type MemberConsentsResponse = z.infer<typeof memberConsentsResponseSchema>;

// `GET /api/v1/internal/members/{ref}/documents/{documentId}/download-url`:
// a presigned S3 GET valid for at most fifteen minutes. The URL is a bearer
// capability — clients open it and never persist it.
export const memberDocumentDownloadUrlDataSchema = z.object({
  url: z.string().url(),
  expiresAt: z.string().datetime({ offset: true }),
});
export type MemberDocumentDownloadUrlData = z.infer<typeof memberDocumentDownloadUrlDataSchema>;

export const memberDocumentDownloadUrlResponseSchema = apiEnvelope(
  memberDocumentDownloadUrlDataSchema,
);
export type MemberDocumentDownloadUrlResponse = z.infer<
  typeof memberDocumentDownloadUrlResponseSchema
>;

// Completed rows (including lapsed booked still stored as booked) that need
// attendance attestation or allow correction — served on a dedicated route so
// the booked-only care-appointments contract stays ADR-135-safe for older
// Atlas bundles. Lapsed booked rows are normalised to status completed here.
export const memberCareAppointmentPendingAttestationSchema = z.object({
  id: z.string().uuid(),
  startsAt: z.string().datetime({ offset: true }),
  durationMinutes: z.number().int().positive(),
  status: z.literal('completed'),
  specialistId: z.string().uuid(),
  specialistName: z.string(),
  isSelf: z.boolean(),
  attendance: careAppointmentAttendanceSchema.nullable(),
  needsAttendance: z.boolean(),
  /** Absent when the server does not know the call type (an unmapped 1.0 import). */
  callType: routableCareCallTypeSchema.optional(),
  /** Present when the booking carries a reason; omitted when general. */
  callReason: tolerantCareCallReasonSchema,
  /** The raw Pelago 1.0 `reason` token, on imported rows only. */
  legacyReason: z.string().nullable().optional(),
});
export type MemberCareAppointmentPendingAttestation = z.infer<
  typeof memberCareAppointmentPendingAttestationSchema
>;

export const memberCareAppointmentsPendingAttestationDataSchema = z.object({
  appointments: z.array(memberCareAppointmentPendingAttestationSchema),
});
export type MemberCareAppointmentsPendingAttestationData = z.infer<
  typeof memberCareAppointmentsPendingAttestationDataSchema
>;

export const memberCareAppointmentsPendingAttestationResponseSchema = apiEnvelope(
  memberCareAppointmentsPendingAttestationDataSchema,
);
export type MemberCareAppointmentsPendingAttestationResponse = z.infer<
  typeof memberCareAppointmentsPendingAttestationResponseSchema
>;

/** Booked upcoming rows plus completed backstop rows after client-side merge. */
export type MemberCareAppointmentListItem =
  | MemberCareAppointment
  | MemberCareAppointmentPendingAttestation;

// Next contact due date: set or clear the care-team-set follow-up date
// (`member_next_contact_due_dates`, member-owned — no `programKey`, unlike
// care-track). `null` clears it; the field name matches the read/list schemas
// above so a caller never has to remember a different name depending on
// whether it's reading or writing the same fact.
export const nextContactDueDateWriteRequestSchema = z
  .object({
    nextContactDueDate: z
      .union([z.string().date(), z.string().datetime({ offset: true })])
      .nullable(),
  })
  .strict();
export type NextContactDueDateWriteRequest = z.infer<typeof nextContactDueDateWriteRequestSchema>;

export const nextContactDueDateWriteResponseSchema = memberMutationReadResponseSchema;
export type NextContactDueDateWriteResponse = z.infer<typeof nextContactDueDateWriteResponseSchema>;

/**
 * Staff append of a member address (ADR-130: the history is append-only, so a
 * correction is a new row rather than an edit of the old one).
 *
 * `.strict()` per ADR-135 Rule 1 — this is a REQUEST, so an unknown key is a
 * caller bug and must fail loudly instead of being silently dropped.
 *
 * `kind` takes the closed enum here, which is the OPPOSITE of the choice made
 * on `memberAddressSchema`, and both are right. On a client-parsed response a
 * closed enum turns a future vocabulary addition into a rejected body; on a
 * request the enum IS the validation, and a value outside it has to be refused
 * rather than quietly substituted with a default kind.
 *
 * `currentAddressId` is the baseline: the id of the newest row the caller
 * composed against for this `(member, kind)`, or `null` when the member has
 * none yet. Required, not optional — an optional baseline lets a client omit
 * its way past the stale-write refusal, which would leave the guarantee
 * unenforceable at the boundary.
 */
export const memberAddressWriteRequestSchema = z
  .object({
    kind: memberAddressKindSchema,
    line1: z.string().trim().min(1).max(200),
    line2: z.string().trim().max(200).nullish(),
    line3: z.string().trim().max(200).nullish(),
    city: z.string().trim().min(1).max(120),
    state: z.string().trim().min(1).max(60),
    postalCode: z.string().trim().min(1).max(16),
    country: z
      .string()
      .trim()
      .regex(COUNTRY_CODE_PATTERN, 'must be an ISO 3166-1 alpha-2 country code')
      .default(DEFAULT_COUNTRY_CODE),
    currentAddressId: z.string().nullable(),
    change: memberDetailChangeSchema,
  })
  .strict();
export type MemberAddressWriteRequest = z.infer<typeof memberAddressWriteRequestSchema>;

/**
 * Staff edit of the member's second contact channel. Never the `members` pair
 * that OTP verifies and Cognito anchors — that is an identity change needing
 * re-verification, not a profile edit.
 *
 * `baseline` carries the values the caller read, so a save composed against a
 * stale form is refused instead of overwriting someone else's correction.
 *
 * The refine rejects a body carrying only the baseline: it would write nothing,
 * and answering 200 would put a change event on the bus for a change that never
 * happened. The write repo refuses the same shape independently — the schema
 * gives the 400, the repo makes it impossible for a later caller to reach the
 * database with an empty patch at all.
 */
export const memberContactWriteRequestSchema = z
  .object({
    profileEmail: z.string().trim().email().max(254).nullish(),
    profilePhoneNumber: e164PhoneSchema.nullish(),
    baseline: z
      .object({
        profileEmail: z.string().nullable(),
        profilePhoneNumber: z.string().nullable(),
      })
      .strict(),
    change: memberDetailChangeSchema,
  })
  .strict()
  .refine((body) => body.profileEmail !== undefined || body.profilePhoneNumber !== undefined, {
    message: 'at least one of profileEmail or profilePhoneNumber must be edited',
  });
export type MemberContactWriteRequest = z.infer<typeof memberContactWriteRequestSchema>;

// NOT `.strict()`, and neither is the nested address — ADR-135 Rule 1. Atlas
// parses these at runtime, so a bundle loaded before a deploy that adds a field
// must ignore the new key rather than reject the whole body.
export const memberAddressWriteDataSchema = z.object({
  address: memberAddressSchema,
});
export type MemberAddressWriteData = z.infer<typeof memberAddressWriteDataSchema>;
export const memberAddressWriteResponseSchema = apiEnvelope(memberAddressWriteDataSchema);
export type MemberAddressWriteResponse = z.infer<typeof memberAddressWriteResponseSchema>;

// Echoes the stored pair so the card can show what actually landed rather than
// what it hoped it sent.
export const memberContactWriteDataSchema = z.object({
  profileEmail: z.string().nullable(),
  profilePhoneNumber: z.string().nullable(),
});
export type MemberContactWriteData = z.infer<typeof memberContactWriteDataSchema>;
export const memberContactWriteResponseSchema = apiEnvelope(memberContactWriteDataSchema);
export type MemberContactWriteResponse = z.infer<typeof memberContactWriteResponseSchema>;
// The staff "this needs no reply" exit on a member's contact case. The response
// is the member projection rather than a case-shaped object: Atlas renders the
// member row directly from it, so a bespoke shape would give it a second thing
// to merge. It is the MUTATION body, like every other write echo on this
// surface — the graduated identity fields (gender identity, pronouns, preferred
// name) are read-only and must not leave the server on a write response.
export const contactCaseCloseResponseSchema = memberMutationReadResponseSchema;
export type ContactCaseCloseResponse = z.infer<typeof contactCaseCloseResponseSchema>;
